import React, { useState, useEffect, useCallback } from 'react';
import {
  getAutomationRules, AutomationListResult,
  getAutomationStats, getAutomationTemplates,
  createAutomationRule, updateAutomationRule, deleteAutomationRule,
  updateRuleStatus, runAutomationRule,
} from '../services/adminService';
import {
  pageContainer, cardStyle, statsGrid, labelStyle, valueStyle,
  subTextStyle, sectionTitle, thStyle, tdStyle, badgeStyle, tableSearch, smallSelect,
} from '../styles/theme';
import { useAdminPage, useDebounce, useToast } from '../utils/useAdminPage';
import Paginator from '../components/Paginator';
import Toast from '../components/Toast';
import PageHeader from '../components/PageHeader';

// ── Constants ────────────────────────────────────────────────────────────────
const EMPTY: AutomationListResult = {
  items: [], summary: { totalRules: 0, activeRules: 0, inactiveRules: 0 },
};

const TRIGGER_OPTIONS = [
  'BOOKING_CREATED', 'BOOKING_ACCEPTED', 'BOOKING_COMPLETED',
  'BOOKING_CANCELLED', 'SCHOLAR_REGISTERED', 'SCHOLAR_APPROVED',
  'PAYMENT_RECEIVED', 'PAYOUT_DUE', 'SUPPORT_TICKET_CREATED',
  'SCHEDULE_DAILY', 'SCHEDULE_WEEKLY',
];

const ACTION_OPTIONS = [
  'SEND_NOTIFICATION', 'SEND_EMAIL', 'AUTO_APPROVE', 'AUTO_CONFIRM',
  'CREATE_PAYOUT', 'ESCALATE_TICKET', 'FLAG_REVIEW',
];

const BLANK_FORM = {
  name: '', description: '', trigger: TRIGGER_OPTIONS[0],
  action: ACTION_OPTIONS[0], status: 'ACTIVE',
};

// ── Rule Form Modal ───────────────────────────────────────────────────────────
interface RuleModalProps {
  mode: 'create' | 'edit';
  initial: typeof BLANK_FORM & { id?: string };
  onSave: (data: any) => Promise<void>;
  onClose: () => void;
  saving: boolean;
}

const RuleModal: React.FC<RuleModalProps> = ({ mode, initial, onSave, onClose, saving }) => {
  const [form, setForm] = useState({ ...initial });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
    backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 9999, animation: 'fadeIn 0.15s ease',
  };
  const modal: React.CSSProperties = {
    background: '#fff', borderRadius: 20, padding: '36px 40px',
    width: 520, maxWidth: '94vw', boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
    animation: 'slideUp 0.2s cubic-bezier(0.34,1.56,0.64,1)',
  };
  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: '1.5px solid #e2e8f0', fontSize: 14, outline: 'none',
    fontFamily: 'Inter, sans-serif', boxSizing: 'border-box',
  };
  const labelS: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6, display: 'block' };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        {/* Icon + title */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, boxShadow: '0 0 0 8px #eff6ff', marginBottom: 12,
          }}></div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>
            {mode === 'create' ? 'Create Automation Rule' : 'Edit Rule'}
          </h2>
          <p style={{ fontSize: 13, color: '#64748b', margin: '6px 0 0' }}>
            {mode === 'create' ? 'Configure a new automated workflow trigger.' : 'Update the automation rule settings.'}
          </p>
        </div>

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelS}>Rule Name *</label>
            <input
              style={fieldStyle}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Auto-approve verified scholars"
            />
          </div>
          <div>
            <label style={labelS}>Description</label>
            <textarea
              style={{ ...fieldStyle, resize: 'vertical', minHeight: 72 } as any}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Describe what this rule does…"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelS}>Trigger Event *</label>
              <select style={fieldStyle} value={form.trigger} onChange={(e) => set('trigger', e.target.value)}>
                {TRIGGER_OPTIONS.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={labelS}>Action Type *</label>
              <select style={fieldStyle} value={form.action} onChange={(e) => set('action', e.target.value)}>
                {ACTION_OPTIONS.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={labelS}>Status</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {['ACTIVE', 'INACTIVE'].map((s) => (
                <button
                  key={s}
                  onClick={() => set('status', s)}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: 10, fontWeight: 700,
                    fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
                    border: form.status === s ? 'none' : '1.5px solid #e2e8f0',
                    background: form.status === s
                      ? (s === 'ACTIVE' ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#ef4444,#dc2626)')
                      : '#f8fafc',
                    color: form.status === s ? '#fff' : '#475569',
                  }}
                >{s === 'ACTIVE' ? ' Active' : '⏸ Inactive'}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1, padding: '12px 0', borderRadius: 12,
              border: '1.5px solid #e2e8f0', background: '#fff',
              color: '#475569', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}
          >Cancel</button>
          <button
            onClick={() => onSave(form)}
            disabled={saving || !form.name.trim()}
            style={{
              flex: 1, padding: '12px 0', borderRadius: 12, border: 'none',
              background: saving || !form.name.trim()
                ? '#93c5fd'
                : 'linear-gradient(135deg,#2563eb,#3b82f6)',
              color: '#fff', fontWeight: 700, fontSize: 14,
              cursor: saving || !form.name.trim() ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 16px rgba(37,99,235,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {saving ? (
              <>
                <span style={{
                  width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#fff', borderRadius: '50%',
                  display: 'inline-block', animation: 'spin 0.7s linear infinite',
                }} />
                Saving…
              </>
            ) : (mode === 'create' ? '✓ Create Rule' : '✓ Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Delete Confirm Modal ──────────────────────────────────────────────────────
const DeleteModal: React.FC<{ rule: any; onConfirm: () => Promise<void>; onCancel: () => void; deleting: boolean }> =
  ({ rule, onConfirm, onCancel, deleting }) => {
    if (!rule) return null;
    const overlay: React.CSSProperties = {
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
      backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 9999, animation: 'fadeIn 0.15s ease',
    };
    const modal: React.CSSProperties = {
      background: '#fff', borderRadius: 20, padding: '36px 40px',
      width: 420, maxWidth: '94vw', boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
      animation: 'slideUp 0.2s cubic-bezier(0.34,1.56,0.64,1)',
    };
    return (
      <div style={overlay} onClick={onCancel}>
        <div style={modal} onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: '#fef2f2', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: 24, boxShadow: '0 0 0 8px #fef2f2', marginBottom: 12,
            }}></div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Delete Rule?</h2>
            <p style={{ fontSize: 13, color: '#64748b', margin: '8px 0 0', lineHeight: 1.6 }}>
              This will permanently delete <strong>{rule.name}</strong>. This action cannot be undone.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
            <button onClick={onCancel} disabled={deleting} style={{
              flex: 1, padding: '12px 0', borderRadius: 12,
              border: '1.5px solid #e2e8f0', background: '#fff',
              color: '#475569', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}>Cancel</button>
            <button onClick={onConfirm} disabled={deleting} style={{
              flex: 1, padding: '12px 0', borderRadius: 12, border: 'none',
              background: deleting ? '#fca5a5' : 'linear-gradient(135deg,#ef4444,#dc2626)',
              color: '#fff', fontWeight: 700, fontSize: 14,
              cursor: deleting ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              {deleting ? (
                <>
                  <span style={{
                    width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: '#fff', borderRadius: '50%',
                    display: 'inline-block', animation: 'spin 0.7s linear infinite',
                  }} />
                  Deleting…
                </>
              ) : ' Delete Rule'}
            </button>
          </div>
        </div>
      </div>
    );
  };

// ── Main Component ────────────────────────────────────────────────────────────
const AutomationRules = () => {
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('');
  const [page, setPage]         = useState(1);
  const [statsData, setStatsData] = useState<any>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const pageSize = 10;

  const dSearch = useDebounce(search, 350);
  const { toast, showToast } = useToast();

  // Modal state
  const [ruleModal, setRuleModal] = useState<{ mode: 'create' | 'edit'; initial: any } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [saving, setSaving]    = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const cacheKey = `${dSearch}|${status}`;

  const { data, loading, revalidating, syncLabel, lastSyncedAt, refresh, mutate } =
    useAdminPage<AutomationListResult>(EMPTY, {
      cacheNs: 'automation:',
      cacheKeySuffix: cacheKey,
      fetcher: () => getAutomationRules({ search: dSearch, status }),
    });

  useEffect(() => {
    Promise.all([getAutomationStats(), getAutomationTemplates()]).then(([s, t]) => {
      if (s) setStatsData(s);
      setTemplates(t || []);
    });
  }, []);

  const items   = data.items || [];
  const summary = data.summary;
  const paged   = items.slice((page - 1) * pageSize, page * pageSize);

  // ── CRUD handlers ────────────────────────────────────────────
  const openCreate = (prefill?: any) =>
    setRuleModal({ mode: 'create', initial: { ...BLANK_FORM, ...prefill } });

  const openEdit = (rule: any) =>
    setRuleModal({
      mode: 'edit',
      initial: {
        id: rule.id,
        name: rule.name || '',
        description: rule.description || '',
        trigger: rule.trigger || TRIGGER_OPTIONS[0],
        action: rule.action || ACTION_OPTIONS[0],
        status: rule.status || 'ACTIVE',
      },
    });

  const handleSave = useCallback(async (formData: any) => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      if (ruleModal?.mode === 'create') {
        await createAutomationRule({
          name: formData.name,
          description: formData.description,
          trigger: formData.trigger,
          action: formData.action,
          status: formData.status,
        });
        showToast(`Rule "${formData.name}" created `);
      } else {
        await updateAutomationRule(formData.id, {
          name: formData.name,
          description: formData.description,
          trigger: formData.trigger,
          action: formData.action,
          status: formData.status,
        });
        showToast(`Rule "${formData.name}" updated ✓`);
      }
      setRuleModal(null);
      await mutate();
      // Refresh stats
      const s = await getAutomationStats();
      if (s) setStatsData(s);
    } catch {
      showToast('Failed to save rule. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }, [ruleModal, mutate, showToast]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAutomationRule(deleteTarget.id);
      showToast(`Rule "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      await mutate();
      const s = await getAutomationStats();
      if (s) setStatsData(s);
    } catch {
      showToast('Failed to delete rule.', 'error');
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, mutate, showToast]);

  const handleToggle = useCallback(async (rule: any) => {
    setTogglingId(rule.id);
    const next = rule.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await updateRuleStatus(rule.id, next);
      showToast(`Rule "${rule.name}" is now ${next.toLowerCase()}`);
      await mutate();
      const s = await getAutomationStats();
      if (s) setStatsData(s);
    } catch {
      showToast('Failed to update rule status.', 'error');
    } finally {
      setTogglingId(null);
    }
  }, [mutate, showToast]);

  const handleRun = useCallback(async (rule: any) => {
    setRunningId(rule.id);
    try {
      await runAutomationRule(rule.id);
      showToast(`Rule "${rule.name}" executed `);
      await mutate();
    } catch {
      showToast('Failed to execute rule.', 'error');
    } finally {
      setRunningId(null);
    }
  }, [mutate, showToast]);

  // ── Stats cards ──────────────────────────────────────────────
  const statCards = [
    { label: 'Total Rules',  val: summary.totalRules.toString(),  sub: 'Configured', icon: '', color: '#2563eb' },
    { label: 'Active',       val: summary.activeRules.toString(), sub: 'Running now', icon: '', color: '#10b981' },
    { label: 'Executions',   val: (statsData?.executionsToday || 0).toLocaleString(), sub: 'All-time runs', icon: '', color: '#8b5cf6' },
    { label: 'Success Rate', val: statsData ? `${Math.round(statsData.successRate || 100)}%` : '—', sub: 'Reliability', icon: '', color: '#f59e0b' },
  ];

  return (
    <div style={pageContainer}>
      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { opacity:0; transform:translateY(20px) scale(0.97) } to { opacity:1; transform:translateY(0) scale(1) } }
        @keyframes spin    { to { transform:rotate(360deg) } }
      `}</style>

      <Toast toast={toast} />

      {/* Modals */}
      {ruleModal && (
        <RuleModal
          mode={ruleModal.mode}
          initial={ruleModal.initial}
          onSave={handleSave}
          onClose={() => !saving && setRuleModal(null)}
          saving={saving}
        />
      )}
      <DeleteModal
        rule={deleteTarget}
        onConfirm={handleDelete}
        onCancel={() => !deleting && setDeleteTarget(null)}
        deleting={deleting}
      />

      {/* Page Header */}
      <PageHeader
        breadcrumb="Dashboard > Automation"
        title="Automation Rules"
        subtitle="Create and manage automated workflows to streamline platform operations."
        syncLabel={syncLabel}
        revalidating={revalidating}
        loading={loading}
        lastSyncedAt={lastSyncedAt}
        onRefresh={refresh}
      />

      {/* ── Stats Grid ── */}
      <div style={statsGrid}>
        {statCards.map((s, i) => (
          <div key={i} style={{ ...cardStyle, position: 'relative', overflow: 'hidden' }}>
            <div style={{
              position: 'absolute', top: -16, right: -16, width: 70, height: 70,
              borderRadius: '50%', background: `${s.color}10`,
            }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={labelStyle}>{s.label}</span>
              <span style={{ fontSize: 20 }}>{s.icon}</span>
            </div>
            <div style={{ ...valueStyle, color: s.color }}>{s.val}</div>
            <div style={subTextStyle}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 28 }}>

        {/* ── Rules Table ── */}
        <div style={cardStyle}>
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 10, flex: 1, flexWrap: 'wrap' }}>
              <input
                type="text" placeholder="Search rules…"
                style={{ ...tableSearch, flex: 1, minWidth: 180 }}
                value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
              <select style={smallSelect} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                <option value="">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
            <button
              onClick={() => openCreate()}
              style={{
                background: 'linear-gradient(135deg,#2563eb,#3b82f6)', color: '#fff',
                border: 'none', padding: '9px 18px', borderRadius: 10,
                fontWeight: 700, fontSize: 13, cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
                whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              ＋ Create Rule
            </button>
          </div>

          {/* Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 11, borderBottom: '2px solid #f1f5f9', letterSpacing: '0.05em' }}>
                <th style={thStyle}>RULE</th>
                <th style={thStyle}>TRIGGER</th>
                <th style={thStyle}>ACTION</th>
                <th style={thStyle}>STATUS</th>
                <th style={thStyle}>RUNS</th>
                <th style={thStyle}>LAST RUN</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: 60, textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, color: '#94a3b8' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', animation: 'spin 0.7s linear infinite' }} />
                    <span style={{ fontSize: 13 }}>Loading rules…</span>
                  </div>
                </td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 60, textAlign: 'center' }}>
                  <div style={{ color: '#94a3b8' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}></div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>No rules found</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      {search || status ? 'Try adjusting your filters.' : 'Create your first automation rule above.'}
                    </div>
                  </div>
                </td></tr>
              ) : paged.map((r: any) => (
                <tr
                  key={r.id}
                  style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.12s' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.description || '—'}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeStyle, whiteSpace: 'nowrap' }}>
                      {(r.trigger || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeStyle, background: '#f5f3ff', color: '#7c3aed', whiteSpace: 'nowrap' }}>
                      {(r.action || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {/* Toggle pill */}
                    <button
                      onClick={() => handleToggle(r)}
                      disabled={togglingId === r.id}
                      title={`Click to ${r.status === 'ACTIVE' ? 'deactivate' : 'activate'}`}
                      style={{
                        padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        border: 'none', cursor: togglingId === r.id ? 'wait' : 'pointer',
                        background: r.status === 'ACTIVE' ? '#f0fdf4' : '#f1f5f9',
                        color: r.status === 'ACTIVE' ? '#15803d' : '#94a3b8',
                        transition: 'all 0.2s',
                      }}
                    >
                      {togglingId === r.id ? '…' : (r.status === 'ACTIVE' ? '● Active' : '○ Inactive')}
                    </button>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{(r.executions || 0).toLocaleString()}</span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {r.lastRunAt ? new Date(r.lastRunAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                      {/* Run Now */}
                      <button
                        onClick={() => handleRun(r)}
                        disabled={runningId === r.id}
                        title="Run Now"
                        style={{
                          padding: '5px 10px', borderRadius: 8, border: 'none',
                          background: runningId === r.id ? '#c7d2fe' : '#eff6ff',
                          color: '#2563eb', fontWeight: 700, fontSize: 11,
                          cursor: runningId === r.id ? 'wait' : 'pointer',
                          transition: 'all 0.15s', whiteSpace: 'nowrap',
                        }}
                      >{runningId === r.id ? '…' : '▶ Run'}</button>
                      {/* Edit */}
                      <button
                        onClick={() => openEdit(r)}
                        title="Edit"
                        style={{
                          padding: '5px 10px', borderRadius: 8, border: 'none',
                          background: '#f1f5f9', color: '#475569',
                          fontWeight: 700, fontSize: 11, cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >️</button>
                      {/* Delete */}
                      <button
                        onClick={() => setDeleteTarget(r)}
                        title="Delete"
                        style={{
                          padding: '5px 10px', borderRadius: 8, border: 'none',
                          background: '#fef2f2', color: '#ef4444',
                          fontWeight: 700, fontSize: 11, cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      ></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Paginator total={items.length} page={page} pageSize={pageSize} onPage={setPage} />
        </div>

        {/* ── Sidebar: Rule Templates ── */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ ...sectionTitle, marginBottom: 0 }}>Rule Templates</h3>
            <span style={{ fontSize: 11, color: '#64748b', background: '#f1f5f9', padding: '3px 8px', borderRadius: 20, fontWeight: 600 }}>
              {templates.length} available
            </span>
          </div>
          <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16, marginTop: -8 }}>
            Click a template to pre-fill the create form.
          </p>
          {templates.length === 0 ? (
            <p style={{ fontSize: 12, color: '#94a3b8' }}>No templates available</p>
          ) : templates.map((t: any, i: number) => {
            const COLORS = ['#eff6ff', '#fff7ed', '#f0fdf4', '#fdf2f2', '#f5f3ff', '#fefce8'];
            const BORDERS = ['#bfdbfe', '#fed7aa', '#bbf7d0', '#fecaca', '#ddd6fe', '#fef08a'];
            return (
              <div
                key={i}
                onClick={() => openCreate({
                  name: t.name,
                  description: t.description,
                  trigger: t.trigger,
                  action: t.action,
                })}
                style={{
                  display: 'flex', gap: 12, padding: 14, borderRadius: 12,
                  border: `1.5px solid ${BORDERS[i % BORDERS.length]}`,
                  marginBottom: 10, cursor: 'pointer',
                  background: COLORS[i % COLORS.length],
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: 'rgba(255,255,255,0.8)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, flexShrink: 0,
                }}>{t.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{t.description}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    <span style={{ ...badgeStyle, fontSize: 10, padding: '2px 7px' }}>
                      {(t.trigger || '').replace(/_/g, ' ')}
                    </span>
                    <span style={{ ...badgeStyle, fontSize: 10, padding: '2px 7px', background: '#f5f3ff', color: '#7c3aed' }}>
                      {(t.action || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Summary stats */}
          {statsData && (
            <div style={{
              marginTop: 20, padding: '16px',
              borderRadius: 12, background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
              border: '1px solid #bfdbfe',
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', marginBottom: 10 }}>Platform Automation Stats</div>
              {[
                { label: 'Time Saved', val: `${statsData.timeSavedHours}h` },
                { label: 'Total Executions', val: (statsData.executionsToday || 0).toLocaleString() },
                { label: 'Success Rate', val: `${statsData.successRate || 0}%` },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{item.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>{item.val}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutomationRules;
